import { useState, useEffect, useCallback } from "react";
import { Rocket } from "lucide-react";
import { api } from "@/services/api";
import { LaunchCard, LaunchCardSkeleton } from "@/components/LaunchCard";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, EmptyState } from "@/components/States";

export default function LaunchesPage() {
  const [launches, setLaunches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLaunches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getLaunches(20);
      setLaunches(res.launches);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLaunches();
    const interval = setInterval(fetchLaunches, 300000);
    return () => clearInterval(interval);
  }, [fetchLaunches]);

  return (
    <div className="min-h-screen" data-testid="launches-page">
      <div className="hero-gradient fixed inset-0 pointer-events-none" />
      
      <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 md:px-8 py-8">
        {/* Header */}
        <PageHeader 
          icon={Rocket}
          title="Florida Launches"
          subtitle="Upcoming rocket launches from Kennedy Space Center & Cape Canaveral"
        />

        {/* Content */}
        {error ? (
          <ErrorState message={error} onRetry={fetchLaunches} />
        ) : loading ? (
          <div className="space-y-4">
            <LaunchCardSkeleton featured />
            <LaunchCardSkeleton />
            <LaunchCardSkeleton />
            <LaunchCardSkeleton />
          </div>
        ) : launches?.length > 0 ? (
          <div className="space-y-4">
            {launches.map((launch, index) => (
              <div key={launch.id} className="stagger-item" style={{ animationDelay: `${index * 0.05}s` }}>
                <LaunchCard launch={launch} featured={index === 0} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No Upcoming Launches"
            message="Check back later for upcoming Florida rocket launches."
            icon={Rocket}
          />
        )}

        <Footer />
      </div>
    </div>
  );
}
